/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import Emitter from 'events';

const emitter = new Emitter();
// max payloads kept per event while no listener is subscribed
const MAX_BUFFERED_EVENTS = 20;
/**
 * define event emitter functions to add, remove, emit events
 * once - add one time listener function for a event
 * on - add listener function for a event
 * off - remove listener function of a event
 * removeAllListeners - remove all event listeners
 * emit - trigger listener function of a event
 */
const EventEmitter = {
  emitter,
  eventBuffer: {},
  listeners: (event) => emitter.listeners(event),
  once: (event, fn) => emitter.once(event, fn),
  subscribe: (event, fn) => {
    emitter.on(event, fn);
    // If there are buffered events, dispatch them
    if (EventEmitter.eventBuffer[event]) {
      EventEmitter.eventBuffer[event].forEach((payload) => {
        emitter.emit(event, payload);
      });
      // Clear the buffer for this event
      EventEmitter.eventBuffer[event] = [];
    }
  },
  unsubscribe: (event, fn) => emitter.off(event, fn),
  unsubscribeAll: () => emitter.removeAllListeners(),
  dispatch: (event, payload) => {
    const listeners = emitter.listeners(event);
    if (listeners.length > 0) {
      // If there are listeners, dispatch the event immediately
      emitter.emit(event, payload);
    } else {
      // If no listeners, buffer the event
      if (!EventEmitter.eventBuffer[event]) {
        EventEmitter.eventBuffer[event] = [];
      }
      EventEmitter.eventBuffer[event].push(payload);
      // Drop the oldest payload so an event nobody listens to cannot grow forever
      if (EventEmitter.eventBuffer[event].length > MAX_BUFFERED_EVENTS) {
        EventEmitter.eventBuffer[event].shift();
        console.warn(`[ui-controller] dropped buffered event "${event}": no listener subscribed`);
      }
    }
  },
};
// Freeze event emitter object
Object.freeze(EventEmitter);
//
export default EventEmitter;
